// Copyright 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
// Please see LICENSE files in the repository root for full details.

// Brand logos for upstream OAuth 2.0 providers. Must be kept in sync with
// templates/components/idp_brand.html, which the server-rendered login page
// still uses.
type Logo = React.FC<React.SVGAttributes<SVGElement>>;

const GoogleLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      d="M24.0022 12.276C24.0022 11.4603 23.9348 10.6402 23.7908 9.83771H12.2429V14.4585H18.8559C18.5814 15.9488 17.6997 17.2672 16.4086 18.1049V21.1032H20.3539C22.6707 19.0132 24.0022 15.9268 24.0022 12.276Z"
      fill="#4285F4"
    />
    <path
      d="M12.2428 24C15.5448 24 18.3294 22.9374 20.3583 21.1032L16.413 18.1049C15.3154 18.8369 13.8983 19.2513 12.2473 19.2513C9.05332 19.2513 6.34517 17.1393 5.37347 14.2998H1.30225V17.3906C3.3806 21.4427 7.61377 24 12.2428 24Z"
      fill="#34A853"
    />
    <path
      d="M5.36907 14.2998C4.85623 12.8095 4.85623 11.1957 5.36907 9.7054V6.61456H1.30234C-0.434114 10.0052 -0.434114 13.9999 1.30234 17.3906L5.36907 14.2998Z"
      fill="#FBBC04"
    />
    <path
      d="M12.2428 4.7495C13.9883 4.72305 15.6753 5.36679 16.9394 6.54845L20.4348 3.12251C18.2215 1.08547 15.2839 -0.0344648 12.2428 0.000808639C7.61377 0.000808639 3.3806 2.55814 1.30225 6.61459L5.36898 9.70543C6.33617 6.8615 9.04883 4.7495 12.2428 4.7495Z"
      fill="#EA4335"
    />
  </svg>
);

const GitLabLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.0002 23.1079L16.4193 9.50749H7.58116L12.0002 23.1079Z"
      fill="#E24329"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.9999 23.1079L7.58081 9.50749H1.38759L11.9999 23.1079Z"
      fill="#FC6D26"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.38772 9.50749L0.0448467 13.6405C-0.0776384 14.0175 0.0565119 14.4305 0.377192 14.6634L12 23.1079L1.38772 9.50749Z"
      fill="#FCA326"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.38759 9.50752H7.58081L4.91919 1.31612C4.78229 0.89457 4.18599 0.894684 4.0491 1.31612L1.38759 9.50752Z"
      fill="#E24329"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.0001 23.1079L16.4192 9.50749H22.6124L12.0001 23.1079Z"
      fill="#FC6D26"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22.6124 9.50749L23.9552 13.6405C24.0777 14.0175 23.9436 14.4305 23.6229 14.6634L12.0001 23.1079L22.6124 9.50749Z"
      fill="#FCA326"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22.6122 9.50752H16.4189L19.0806 1.31612C19.2175 0.89457 19.8138 0.894684 19.9507 1.31612L22.6122 9.50752Z"
      fill="#E24329"
    />
  </svg>
);

const TwitterLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 25 24"
    fill="none"
    {...props}
  >
    <path
      d="M9.04155 21C6.6153 21 4.35363 20.2943 2.45 19.0767C4.06624 19.1813 6.91855 18.9308 8.69268 17.2386C6.0238 17.1161 4.82019 15.0692 4.6632 14.1945C4.88997 14.2819 5.97147 14.3869 6.582 14.142C3.51192 13.3722 3.04094 10.678 3.1456 9.85573C3.72124 10.2581 4.69809 10.3981 5.08185 10.3631C2.22109 8.31618 3.25027 5.23707 3.75613 4.57226C5.80911 7.4165 8.8859 9.01393 12.6923 9.10278C12.6205 8.78802 12.5826 8.46032 12.5826 8.12373C12.5826 5.70819 14.5351 3.75 16.9435 3.75C18.2019 3.75 19.3358 4.28457 20.1318 5.13963C20.9727 4.94258 22.2382 4.4813 22.8569 4.0824C22.5451 5.20208 21.5742 6.13612 20.9869 6.48231C20.9918 6.49408 20.9821 6.47048 20.9869 6.48231C21.5028 6.40428 22.8986 6.13603 23.45 5.76192C23.1773 6.39094 22.148 7.4368 21.3033 8.02232C21.4604 14.9535 16.1574 21 9.04155 21Z"
      fill="#1D9BF0"
    />
  </svg>
);

const GitHubLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.9642 0C5.34833 0 0 5.38776 0 12.0531C0 17.3811 3.42686 21.8912 8.18082 23.4874C8.77518 23.6074 8.9929 23.2281 8.9929 22.909C8.9929 22.6296 8.97331 21.6718 8.97331 20.6738C5.64514 21.3923 4.95208 19.237 4.95208 19.237C4.41722 17.8401 3.62473 17.4811 3.62473 17.4811C2.53543 16.7427 3.70408 16.7427 3.70408 16.7427C4.91241 16.8225 5.54645 17.9799 5.54645 17.9799C6.61592 19.8157 8.33926 19.297 9.03257 18.9776C9.13151 18.1993 9.44865 17.6606 9.78539 17.3613C7.13094 17.0819 4.33812 16.0442 4.33812 11.4144C4.33812 10.0974 4.81322 9.01984 5.56604 8.1818C5.44727 7.88253 5.03118 6.64506 5.68506 4.98882C5.68506 4.98882 6.69527 4.66947 8.97306 6.22604C9.94827 5.9622 10.954 5.82799 11.9642 5.82686C12.9744 5.82686 14.0042 5.96669 14.9552 6.22604C17.2332 4.66947 18.2434 4.98882 18.2434 4.98882C18.8973 6.64506 18.481 7.88253 18.3622 8.1818C19.1349 9.01984 19.5904 10.0974 19.5904 11.4144C19.5904 16.0442 16.7976 17.0618 14.1233 17.3613C14.5592 17.7404 14.9353 18.4587 14.9353 19.5962C14.9353 21.2126 14.9158 22.5098 14.9158 22.9087C14.9158 23.2281 15.1337 23.6074 15.7278 23.4877C20.4818 21.8909 23.9087 17.3811 23.9087 12.0531C23.9282 5.38776 18.5603 0 11.9642 0Z"
      fill="currentColor"
    />
  </svg>
);

const FacebookLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 25 24"
    fill="none"
    {...props}
  >
    <path
      d="M10.02 23.88C4.32 22.86 0 17.94 0 12C0 5.4 5.4 0 12 0C18.6 0 24 5.4 24 12C24 17.94 19.68 22.86 13.98 23.88L13.32 23.34H10.68L10.02 23.88Z"
      fill="url(#mas-facebook-logo-gradient)"
    />
    <path
      d="M16.68 15.36L17.22 12H14.04V9.66C14.04 8.7 14.4 7.98 15.84 7.98H17.4V4.92C16.56 4.8 15.6 4.68 14.76 4.68C12 4.68 10.08 6.36 10.08 9.36V12H7.08V15.36H10.08V23.82C10.74 23.94 11.4 24 12.06 24C12.72 24 13.38 23.94 14.04 23.82V15.36H16.68Z"
      fill="white"
    />
    <defs>
      <linearGradient
        id="mas-facebook-logo-gradient"
        x1="12.0006"
        y1="23.1654"
        x2="12.0006"
        y2="-0.00442066"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0062E0" />
        <stop offset="1" stopColor="#19AFFF" />
      </linearGradient>
    </defs>
  </svg>
);

const AppleLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 28"
    fill="none"
    {...props}
  >
    <path
      d="M20.9144 8.1816C20.7752 8.2896 18.3176 9.6744 18.3176 12.7536C18.3176 16.3152 21.4448 17.5752 21.5384 17.6064C21.524 17.6832 21.0416 19.332 19.8896 21.012C18.8624 22.4904 17.7896 23.9664 16.1576 23.9664C14.5256 23.9664 14.1056 23.0184 12.2216 23.0184C10.3856 23.0184 9.7328 23.9976 8.24 23.9976C6.7472 23.9976 5.7056 22.6296 4.508 20.9496C3.1208 18.9768 2 15.912 2 13.0032C2 8.3376 5.0336 5.8632 8.0192 5.8632C9.6056 5.8632 10.928 6.9048 11.924 6.9048C12.872 6.9048 14.3504 5.8008 16.1552 5.8008C16.8392 5.8008 19.2968 5.8632 20.9144 8.1816ZM15.2984 3.8256C16.0448 2.94 16.5728 1.7112 16.5728 0.4824C16.5728 0.312 16.5584 0.1392 16.5272 0C15.3128 0.0456 13.868 0.8088 12.9968 1.8192C12.3128 2.5968 11.6744 3.8256 11.6744 5.0712C11.6744 5.2584 11.7056 5.4456 11.72 5.5056C11.7968 5.52 11.9216 5.5368 12.0464 5.5368C13.136 5.5368 14.5064 4.8072 15.2984 3.8256Z"
      fill="currentColor"
    />
  </svg>
);

const DiscordLogo: Logo = (props) => (
  <svg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill="currentColor"
      d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.1.1 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.1 16.1 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.25-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02M8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12m6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12"
    />
  </svg>
);

/** The logo of a brand, sized and labelled by the caller. */
export const providerLogo = (brand: string | null): Logo | undefined => {
  switch (brand) {
    case "google":
      return GoogleLogo;
    case "gitlab":
      return GitLabLogo;
    case "twitter":
      return TwitterLogo;
    case "github":
      return GitHubLogo;
    case "facebook":
      return FacebookLogo;
    case "apple":
      return AppleLogo;
    case "discord":
      return DiscordLogo;
    default:
      return undefined;
  }
};
